// src/components/marketing/CampaignDetail.tsx
// Track E2.2 — read-focused detail view for one Meta Ads campaign.
//
// Loads /admin/campaigns/{id} (summary + ad_sets + performance_history) and
// /admin/campaigns/{id}/metrics (daily time series) in parallel. Renders:
//   · Header: back, name, status, error banner, primary actions
//   · Stat cards: lifetime totals + derived metrics (CTR, CPC, CPM)
//   · Time-series chart: one selectable metric × range (7d / 30d / 90d)
//   · Ad sets accordion: adset + nested ads with meta_*_id badges
//
// Budget editing and creative management are deferred to E2.3 (wizard) —
// this view keeps the surface read-heavy so the metrics chart validates the
// A5 sync end-to-end before we grow the edit surface.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, ArrowLeft, ChevronDown, ChevronRight,
  Copy, ExternalLink, Loader2, PauseCircle, Play, RefreshCw, Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  ApiError,
  getCampaign,
  launchCampaign,
  listCampaignMetrics,
  pauseCampaign,
  resumeCampaign,
  syncCampaign,
  type AdSetDetail,
  type Campaign,
  type CampaignDailyMetric,
  type CampaignDetail as CampaignDetailDTO,
  type CampaignStatus,
} from '@/lib/api_campaigns';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props {
  campaignId: number;
  addNotification?: Notifier;
}

type DateRange = 7 | 30 | 90;

type MetricKey =
  | 'spend_cents'
  | 'impressions'
  | 'clicks'
  | 'conversions'
  | 'reach'
  | 'ctr';

const METRIC_LABELS: Record<MetricKey, { label: string; fmt: (v: number) => string }> = {
  spend_cents:  { label: 'Gasto',       fmt: (v) => `$${(v / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}` },
  impressions:  { label: 'Impresiones', fmt: (v) => v.toLocaleString() },
  clicks:       { label: 'Clicks',      fmt: (v) => v.toLocaleString() },
  conversions:  { label: 'Conversiones',fmt: (v) => v.toLocaleString() },
  reach:        { label: 'Alcance',     fmt: (v) => v.toLocaleString() },
  ctr:          { label: 'CTR',         fmt: (v) => `${v.toFixed(2)}%` },
};

export function CampaignDetail({ campaignId, addNotification }: Props) {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignDetailDTO | null>(null);
  const [metrics, setMetrics] = useState<CampaignDailyMetric[]>([]);
  const [range, setRange] = useState<DateRange>(30);
  const [metric, setMetric] = useState<MetricKey>('spend_cents');
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'launch' | 'pause' | 'resume' | 'sync' | null>(null);
  const [openAdSets, setOpenAdSets] = useState<Set<number>>(new Set());

  // Load the campaign summary once; reload after actions that change state.
  const loadCampaign = useCallback(async () => {
    setError(null);
    try {
      const data = await getCampaign(campaignId);
      setCampaign(data);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      setError(msg);
      addNotification?.({ type: 'error', title: 'No se pudo cargar la campaña', message: msg });
    }
  }, [campaignId, addNotification]);

  // Reload daily metrics whenever the range changes.
  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const rows = await listCampaignMetrics(campaignId, { days: range });
      setMetrics(rows);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      addNotification?.({
        type: 'error', title: 'No se pudieron cargar las métricas',
        message: msg,
      });
    } finally {
      setMetricsLoading(false);
    }
  }, [campaignId, range, addNotification]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      await Promise.all([loadCampaign(), loadMetrics()]);
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [loadCampaign, loadMetrics]);

  const runAction = async (action: 'launch' | 'pause' | 'resume' | 'sync') => {
    setPending(action);
    try {
      switch (action) {
        case 'launch':  await launchCampaign(campaignId); break;
        case 'pause':   await pauseCampaign(campaignId); break;
        case 'resume':  await resumeCampaign(campaignId); break;
        case 'sync':    await syncCampaign(campaignId, 'last_30d'); break;
      }
      addNotification?.({
        type: 'success',
        title: `${ACTION_LABEL[action]} encolada`,
        message: action === 'sync'
          ? 'Las métricas se actualizan cuando el worker corre.'
          : 'Revisa el estado en unos segundos.',
        duration: 4000,
      });
      // Reload summary + metrics — sync especially updates total_* + chart.
      await Promise.all([loadCampaign(), loadMetrics()]);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      addNotification?.({
        type: 'error',
        title: `No se pudo ${ACTION_LABEL[action].toLowerCase()}`,
        message: msg,
      });
    } finally {
      setPending(null);
    }
  };

  if (loading && !campaign) {
    return (
      <div className="o-card p-10 text-center opacity-70">
        <Loader2 className="inline animate-spin" size={20} />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="o-card p-8 text-center">
        <AlertTriangle size={28} className="mx-auto mb-3 text-red-300" />
        <p className="text-white/80 font-semibold">Campaña no encontrada</p>
        {error && <p className="text-sm text-white/50 mt-1">{error}</p>}
        <button
          onClick={() => navigate('/m/mkt-meta/campaigns')}
          className="mt-4 text-sm underline text-[var(--acc1,#00e5ff)]"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  const canLaunch = ['draft', 'error', 'paused'].includes(campaign.status);
  const canPause = !!campaign.meta_campaign_id && campaign.status === 'active';
  const canResume = !!campaign.meta_campaign_id && campaign.status === 'paused';
  const canSync = !!campaign.meta_campaign_id;

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="o-card p-5">
        <div className="flex flex-wrap items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/m/mkt-meta/campaigns')}
            className="o-btn-ghost p-2 rounded-md text-white/70 hover:text-white"
            title="Volver a la lista"
          >
            <ArrowLeft size={16} />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {campaign.is_boost && (
                <Zap size={14} className="text-amber-300" aria-label="Boost" />
              )}
              <h1 className="text-xl font-semibold truncate">{campaign.name}</h1>
              <StatusBadge status={campaign.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-white/50">
              <span>{campaign.objective || 'sin objetivo'}</span>
              {campaign.ad_account_name && <span>· {campaign.ad_account_name}</span>}
              {campaign.meta_campaign_id && (
                <MetaIdBadge id={campaign.meta_campaign_id} />
              )}
              <span>· creada {formatDate(campaign.created_at)}</span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <ActionBtn
              Icon={Play}
              label="Lanzar"
              disabled={!canLaunch || !!pending}
              busy={pending === 'launch'}
              onClick={() => runAction('launch')}
            />
            <ActionBtn
              Icon={PauseCircle}
              label="Pausar"
              disabled={!canPause || !!pending}
              busy={pending === 'pause'}
              onClick={() => runAction('pause')}
            />
            <ActionBtn
              Icon={Activity}
              label="Reanudar"
              disabled={!canResume || !!pending}
              busy={pending === 'resume'}
              onClick={() => runAction('resume')}
            />
            <ActionBtn
              Icon={RefreshCw}
              label="Sync"
              disabled={!canSync || !!pending}
              busy={pending === 'sync'}
              onClick={() => runAction('sync')}
            />
          </div>
        </div>

        {campaign.error_message && (
          <div className="mt-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-semibold">Último error de Meta</div>
              <div className="opacity-80 break-words">{campaign.error_message}</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Stat cards ──────────────────────────────────────── */}
      <StatCards campaign={campaign} />

      {/* ── Time-series chart ───────────────────────────────── */}
      <div className="o-card p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="text-base font-semibold">Tendencia</h2>
          <div className="flex-1" />
          <MetricSelect value={metric} onChange={setMetric} />
          <RangeSelect value={range} onChange={setRange} />
          <button
            type="button"
            onClick={loadMetrics}
            disabled={metricsLoading}
            className="o-btn-ghost p-1.5 rounded-md text-white/70 hover:text-white"
            title="Recargar datos del chart"
          >
            <RefreshCw size={14} className={metricsLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        <MetricsChart metrics={metrics} metric={metric} loading={metricsLoading} />
      </div>

      {/* ── Ad sets accordion ───────────────────────────────── */}
      <AdSetsSection
        adSets={campaign.ad_sets}
        openIds={openAdSets}
        onToggle={(id) => {
          setOpenAdSets((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          });
        }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Stat cards
// ═══════════════════════════════════════════════════════════════

function StatCards({ campaign }: { campaign: Campaign }) {
  const ctr = campaign.total_impressions
    ? (campaign.total_clicks / campaign.total_impressions) * 100
    : null;
  const cpcCents = campaign.total_clicks
    ? Math.round(campaign.total_spend_cents / campaign.total_clicks)
    : null;
  const cpmCents = campaign.total_impressions
    ? Math.round((campaign.total_spend_cents / campaign.total_impressions) * 1000)
    : null;

  const cards: Array<{ label: string; value: string; hint?: string }> = [
    { label: 'Gasto total', value: formatCents(campaign.total_spend_cents) },
    { label: 'Presupuesto diario', value: formatCents(campaign.daily_budget_cents), hint: campaign.lifetime_budget_cents ? `Total: ${formatCents(campaign.lifetime_budget_cents)}` : undefined },
    { label: 'Impresiones', value: campaign.total_impressions.toLocaleString() },
    { label: 'Clicks', value: campaign.total_clicks.toLocaleString(), hint: ctr !== null ? `CTR ${ctr.toFixed(2)}%` : undefined },
    { label: 'Conversiones', value: campaign.total_conversions.toLocaleString() },
    { label: 'CPC', value: cpcCents !== null ? formatCents(cpcCents) : '—', hint: cpmCents !== null ? `CPM ${formatCents(cpmCents)}` : undefined },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map((c) => (
        <div key={c.label} className="o-card p-3">
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            {c.label}
          </div>
          <div className="mt-1 text-lg font-semibold">{c.value}</div>
          {c.hint && (
            <div className="text-[11px] text-white/50 mt-0.5">{c.hint}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Chart
// ═══════════════════════════════════════════════════════════════

function MetricsChart({
  metrics,
  metric,
  loading,
}: {
  metrics: CampaignDailyMetric[];
  metric: MetricKey;
  loading: boolean;
}) {
  const data = useMemo(() => {
    // listCampaignMetrics returns newest-first; charts read oldest-first.
    const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));
    return sorted.map((row) => ({
      date: row.date,
      spend_cents: row.spend_cents,
      impressions: row.impressions,
      clicks: row.clicks,
      conversions: row.conversions,
      reach: row.reach,
      ctr: row.ctr ?? 0,
    }));
  }, [metrics]);

  if (!loading && data.length === 0) {
    return (
      <div className="h-[240px] flex flex-col items-center justify-center text-sm text-white/50">
        <Activity className="opacity-40 mb-2" />
        <p>Sin métricas en el rango seleccionado.</p>
        <p className="text-xs">
          Usa <strong>Sync</strong> arriba para pedirle a Meta las últimas cifras.
        </p>
      </div>
    );
  }

  const { label, fmt } = METRIC_LABELS[metric];

  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis
            dataKey="date"
            stroke="rgba(255,255,255,0.4)"
            tick={{ fontSize: 11 }}
            tickFormatter={(d: string) => d.slice(5)} /* MM-DD */
          />
          <YAxis
            stroke="rgba(255,255,255,0.4)"
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => (metric === 'spend_cents' ? `$${(v / 100).toFixed(0)}` : abbrev(v))}
            width={46}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(20,22,32,0.95)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(value: number) => [fmt(value), label]}
            labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
          />
          <Line
            type="monotone"
            dataKey={metric}
            stroke="var(--acc1, #00e5ff)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={!loading}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function MetricSelect({
  value, onChange,
}: {
  value: MetricKey;
  onChange: (v: MetricKey) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as MetricKey)}
      className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm"
    >
      {(Object.entries(METRIC_LABELS) as Array<[MetricKey, typeof METRIC_LABELS[MetricKey]]>).map(
        ([k, v]) => (
          <option key={k} value={k} className="bg-[var(--panel,#1e1f2b)]">
            {v.label}
          </option>
        ),
      )}
    </select>
  );
}

function RangeSelect({
  value, onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  return (
    <div className="flex items-center rounded-md bg-white/5 border border-white/10 text-sm overflow-hidden">
      {([7, 30, 90] as DateRange[]).map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          className={`px-2 py-1 ${
            value === d ? 'bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)]' : 'text-white/60 hover:text-white'
          }`}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Ad sets accordion
// ═══════════════════════════════════════════════════════════════

function AdSetsSection({
  adSets,
  openIds,
  onToggle,
}: {
  adSets: AdSetDetail[];
  openIds: Set<number>;
  onToggle: (id: number) => void;
}) {
  if (adSets.length === 0) {
    return (
      <div className="o-card p-6 text-center text-sm text-white/50">
        Esta campaña no tiene ad sets todavía.
      </div>
    );
  }

  return (
    <div className="o-card">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
        <h2 className="text-base font-semibold">Ad sets ({adSets.length})</h2>
      </div>
      <div className="divide-y divide-white/5">
        {adSets.map((adset) => {
          const open = openIds.has(adset.id);
          return (
            <div key={adset.id}>
              <button
                type="button"
                onClick={() => onToggle(adset.id)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-left"
              >
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{adset.name}</span>
                    <StatusBadge status={adset.status} />
                  </div>
                  <div className="text-xs text-white/50 mt-0.5 flex flex-wrap gap-x-3">
                    {adset.bid_strategy && <span>{adset.bid_strategy}</span>}
                    <span>Diario: {formatCents(adset.daily_budget_cents)}</span>
                    {adset.meta_adset_id && <MetaIdBadge id={adset.meta_adset_id} />}
                  </div>
                </div>
                <span className="text-xs text-white/40 shrink-0">
                  {adset.ads.length} ad{adset.ads.length === 1 ? '' : 's'}
                </span>
              </button>
              {open && (
                <div className="px-5 pb-4">
                  {adset.ads.length === 0 ? (
                    <div className="text-sm text-white/40 py-2 pl-6">
                      Sin ads en este adset.
                    </div>
                  ) : (
                    <table className="w-full text-sm mt-2">
                      <thead className="text-xs uppercase tracking-wider text-white/40">
                        <tr>
                          <th className="text-left py-1 pl-6">Ad</th>
                          <th className="text-left py-1">Estado</th>
                          <th className="text-right py-1">Impr.</th>
                          <th className="text-right py-1">Clicks</th>
                          <th className="text-right py-1">Gasto</th>
                          <th className="text-right py-1">CTR</th>
                          <th className="text-right py-1">Meta ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adset.ads.map((ad) => {
                          const ctr = ad.impressions ? (ad.clicks / ad.impressions) * 100 : null;
                          return (
                            <tr key={ad.id} className="border-t border-white/5">
                              <td className="py-1.5 pl-6 truncate max-w-[240px]">{ad.name}</td>
                              <td className="py-1.5"><StatusBadge status={ad.status} /></td>
                              <td className="py-1.5 text-right">{ad.impressions.toLocaleString()}</td>
                              <td className="py-1.5 text-right">{ad.clicks.toLocaleString()}</td>
                              <td className="py-1.5 text-right">{formatCents(ad.spend_cents)}</td>
                              <td className="py-1.5 text-right">{ctr !== null ? `${ctr.toFixed(2)}%` : '—'}</td>
                              <td className="py-1.5 text-right">
                                {ad.meta_ad_id ? <MetaIdBadge id={ad.meta_ad_id} tiny /> : <span className="text-white/30">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Shared bits
// ═══════════════════════════════════════════════════════════════

const ACTION_LABEL: Record<'launch' | 'pause' | 'resume' | 'sync', string> = {
  launch: 'Lanzar',
  pause: 'Pausar',
  resume: 'Reanudar',
  sync: 'Sincronizar',
};

const STATUS_STYLES: Record<CampaignStatus, { bg: string; label: string }> = {
  draft:     { bg: 'bg-white/10 text-white/70',          label: 'Borrador' },
  pending:   { bg: 'bg-amber-500/15 text-amber-300',     label: 'Pendiente' },
  active:    { bg: 'bg-emerald-500/15 text-emerald-300', label: 'Activa' },
  paused:    { bg: 'bg-sky-500/15 text-sky-300',         label: 'Pausada' },
  completed: { bg: 'bg-purple-500/15 text-purple-300',   label: 'Completada' },
  error:     { bg: 'bg-red-500/15 text-red-300',         label: 'Error' },
};

function StatusBadge({ status }: { status: CampaignStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${s.bg}`}>
      {s.label}
    </span>
  );
}

function ActionBtn({
  Icon, label, onClick, disabled, busy,
}: {
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
        disabled
          ? 'opacity-30 cursor-not-allowed'
          : 'bg-white/5 border border-white/10 hover:bg-white/10'
      }`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function MetaIdBadge({ id, tiny }: { id: string; tiny?: boolean }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(id).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 font-mono ${tiny ? 'text-[10px]' : 'text-[11px]'} text-white/40 hover:text-white`}
      title={`Copiar ${id}`}
    >
      <ExternalLink size={tiny ? 10 : 11} />
      <span className="truncate max-w-[160px]">{id}</span>
      {copied ? <span className="text-emerald-300">✓</span> : <Copy size={tiny ? 10 : 11} />}
    </button>
  );
}

function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function abbrev(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export default CampaignDetail;
